import { useCallback, useEffect, useState } from "react";
import {
  deleteSsoConfiguration,
  listSsoConfigurations,
  upsertSsoConfiguration,
  type SsoConfiguration,
  type SsoIdpProvider,
} from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * /admin/sso — Plan §21 Phase 5 SSO admin console.
 *
 * Lists all configured SSO domains, lets admins create/edit/enable/
 * delete them. v1 is admin-only (`req.user.plan === "admin"`).
 *
 * v1 ships with a stub SAML provider — the actual login flow ends in a
 * 501 SSO_NOT_IMPLEMENTED response. The note below is shown to admins
 * so they know what to expect when they paste IdP metadata in.
 */
export function AdminSsoPage(): JSX.Element {
  const { user } = useAuth();
  const [rows, setRows] = useState<SsoConfiguration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<SsoConfiguration> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setRows(await listSsoConfigurations());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (user?.plan === "admin") void refresh();
  }, [user, refresh]);

  if (!user) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-8 text-center text-slate-300">
        Sign in to access SSO administration.
      </div>
    );
  }
  if (user.plan !== "admin") {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-8 text-center text-slate-300">
        SSO administration is restricted to admin accounts. Your current
        plan is <code className="font-mono">{user.plan}</code>.
      </div>
    );
  }

  async function handleSave(): Promise<void> {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      await upsertSsoConfiguration({
        domain: editing.domain ?? "",
        idpProvider: (editing.idpProvider ?? "generic-saml") as SsoIdpProvider,
        entityId: editing.entityId ?? "",
        ssoUrl: editing.ssoUrl ?? "",
        x509CertPem: editing.x509CertPem ?? "",
        enabled: editing.enabled ?? false,
      });
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(domain: string): Promise<void> {
    if (
      !window.confirm(
        `Delete SSO configuration for "${domain}"?\n\nUsers from this domain will fall back to OAuth (Google / GitHub) sign-in.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await deleteSsoConfiguration(domain);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">SSO administration</h1>
        <p className="mt-1 text-sm text-slate-400">
          Plan §21 Phase 5. Map email domains to SAML 2.0 Identity
          Providers (Google Workspace, Okta, Microsoft Entra, generic
          SAML). Users from configured domains see a "Continue with SSO"
          button on the sign-in page.
        </p>
      </header>

      <div className="mb-4 rounded-md border border-amber-700/50 bg-amber-900/20 p-3 text-xs text-amber-200">
        <strong>v1 limitation:</strong> the configuration UI + domain
        discovery are live, but the actual SAML login flow returns{" "}
        <code className="font-mono">501 SSO_NOT_IMPLEMENTED</code>. The
        cryptographic verification of SAML assertions (XML-DSig, audience
        validation, replay protection) lands in v2 after dependency
        security review.
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
          {error}
        </div>
      )}

      <div className="mb-4 flex justify-between items-center">
        <p className="text-xs text-slate-500">
          {rows === null
            ? "Loading…"
            : `${rows.length} configuration${rows.length === 1 ? "" : "s"}`}
        </p>
        <button
          type="button"
          onClick={() =>
            setEditing({ domain: "", idpProvider: "generic-saml", enabled: false })
          }
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
        >
          + Add SSO configuration
        </button>
      </div>

      {rows && rows.length === 0 && !editing && (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-sm text-slate-400">
          No SSO configurations yet. Click "Add SSO configuration" to set up
          a domain for SAML sign-in.
        </div>
      )}

      {rows && rows.length > 0 && (
        <ul className="mb-4 divide-y divide-slate-800 rounded-md border border-slate-800">
          {rows.map((r) => (
            <li
              key={r.domain}
              className="flex items-center gap-3 px-3 py-3 text-sm"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <code className="font-mono font-medium text-slate-100">
                    {r.domain}
                  </code>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${idpClass(
                      r.idpProvider,
                    )}`}
                  >
                    {r.idpProvider}
                  </span>
                  {r.enabled ? (
                    <span className="rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-200">
                      enabled
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                      disabled
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-slate-500 truncate">
                  {r.ssoUrl || <span className="italic">no SSO URL set</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditing(r)}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                edit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleDelete(r.domain)}
                className="rounded border border-rose-800 px-2 py-1 text-xs text-rose-300 hover:bg-rose-900/30 disabled:opacity-50"
              >
                delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <section className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
          <h2 className="mb-3 text-lg font-semibold">
            {editing.domain ? `Edit ${editing.domain}` : "New SSO configuration"}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email domain (e.g. acme.com)">
              <input
                type="text"
                value={editing.domain ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, domain: e.target.value })
                }
                placeholder="acme.com"
                disabled={Boolean(rows?.find((r) => r.domain === editing.domain))}
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 disabled:opacity-60"
              />
            </Field>
            <Field label="IdP">
              <select
                value={editing.idpProvider ?? "generic-saml"}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    idpProvider: e.target.value as SsoIdpProvider,
                  })
                }
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
              >
                <option value="google-workspace">Google Workspace</option>
                <option value="okta">Okta</option>
                <option value="entra">Microsoft Entra</option>
                <option value="generic-saml">Generic SAML 2.0</option>
              </select>
            </Field>
            <Field label="IdP Entity ID">
              <input
                type="text"
                value={editing.entityId ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, entityId: e.target.value })
                }
                placeholder="https://accounts.google.com/o/saml2?idpid=…"
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              />
            </Field>
            <Field label="SSO URL (must be https://)">
              <input
                type="url"
                value={editing.ssoUrl ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, ssoUrl: e.target.value })
                }
                placeholder="https://accounts.google.com/o/saml2/idp?…"
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              />
            </Field>
            <div className="col-span-2">
              <Field label="x509 signing certificate (PEM)">
                <textarea
                  value={editing.x509CertPem ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, x509CertPem: e.target.value })
                  }
                  rows={6}
                  placeholder="-----BEGIN CERTIFICATE-----
MIIDdDCCAlygAwIBAgIG…
-----END CERTIFICATE-----"
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100"
                />
              </Field>
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(editing.enabled)}
                onChange={(e) =>
                  setEditing({ ...editing, enabled: e.target.checked })
                }
              />
              Enabled — users from this domain will be offered SSO sign-in
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={busy || !editing.domain}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function idpClass(p: SsoIdpProvider): string {
  switch (p) {
    case "google-workspace":
      return "bg-sky-900/40 text-sky-200 border border-sky-700/60";
    case "okta":
      return "bg-indigo-900/50 text-indigo-200 border border-indigo-700/70";
    case "entra":
      return "bg-cyan-900/40 text-cyan-200 border border-cyan-700/60";
    case "generic-saml":
      return "bg-slate-800 text-slate-300 border border-slate-700";
  }
}
